import { Button, XIconButton } from '@capsule/consumer-design-system';

import { css } from 'styled-components';

import offerIcon from 'public/credit-offer.svg';
import refigerationIcon from 'public/snowflake.svg';
import type { HomeBannerV1 } from 'src/app/client/models/banner';
import { orderTracking } from 'src/app/router/paths';
import type { CTA } from 'src/app/types';
import { CTATypes } from 'src/consts';
import { ORDER_BANNER_CHAT_CTA, ORDER_BANNER_CTA, REFIGERATION_BANNER } from 'src/consts/testIDs';

import { PrecedingIconForBanner, StyledBanner, StyledLinkButton } from './styles';

interface RootBannerProps {
  titleText: string;
  subtitleText: string | null;
  onClick: () => void;
  cta: CTA | null;
}

interface CreditBannerProps extends Omit<RootBannerProps, 'subtitleText' | 'onClick' | 'cta'> {}
export const CreditBanner = ({ titleText }: CreditBannerProps) => (
  <StyledBanner isAlertBanner={true} titleText={titleText} precedingIcon={<PrecedingIconForBanner src={offerIcon} />} />
);

interface AlertBannerProps extends Omit<RootBannerProps, 'subtitleText' | 'cta'> {}
export const AlertBanner = ({ titleText, onClick }: AlertBannerProps) => (
  <StyledBanner isAlertBanner={true} titleText={titleText}>
    <XIconButton
      css={css`
        color: ${({ theme }) => theme.colors.white};
      `}
      helperText="dismiss banner"
      onClick={onClick}
    />
  </StyledBanner>
);

const CtaTypeToBannerButtonText: { [ctaKey: string]: string } = {
  [CTATypes.rescheduleOrderInvalidAddress]: 'RESCHEDULE',
  [CTATypes.openChat]: 'RESCHEDULE',
  [CTATypes.rescheduleOrder]: 'RESCHEDULE',
  [CTATypes.scheduleDelivery]: 'REVIEW',
};

interface OrderBannerProps extends RootBannerProps {}
export const OrderBanner = ({ titleText, subtitleText, cta, onClick }: OrderBannerProps) => {
  const ctaType = cta?.action?.type;
  const buttonText = ctaType && ctaType in CtaTypeToBannerButtonText ? CtaTypeToBannerButtonText[ctaType] : 'VIEW';
  return (
    <StyledBanner isAlertBanner={false} titleText={titleText} subtitleText={subtitleText}>
      {cta?.action?.type === CTATypes.openChat ? (
        <Button
          variant="dark-outline-md"
          onClick={onClick}
          css={css`
            color: white;
          `}
          data-testid={ORDER_BANNER_CHAT_CTA}
        >
          {buttonText}
        </Button>
      ) : (
        <StyledLinkButton
          to={orderTracking.toPath({ orderId: cta?.action?.metadata?.orderUUID ?? '' })}
          style="orderTracking"
          size="smallWide"
          onClick={onClick}
          block
          data-testid={ORDER_BANNER_CTA}
        >
          {buttonText}
        </StyledLinkButton>
      )}
    </StyledBanner>
  );
};

interface RefigerationBannerProps extends Omit<RootBannerProps, 'subtitleText' | 'onClick' | 'cta'> {}
export const RefigerationBanner = ({ titleText }: RefigerationBannerProps) => (
  <StyledBanner
    isAlertBanner={true}
    titleText={titleText}
    precedingIcon={<PrecedingIconForBanner src={refigerationIcon} data-testid={REFIGERATION_BANNER} />}
  />
);

export const HomeBanner = ({
  homeBanner,
  hasAlertBannerBeenDismissed,
  onCallToActionClick,
}: {
  homeBanner: HomeBannerV1;
  hasAlertBannerBeenDismissed: boolean;
  onCallToActionClick: () => void;
}) => {
  const ctaType = homeBanner.cta?.action?.type;
  if (homeBanner.showDismissOption && !hasAlertBannerBeenDismissed) {
    return <AlertBanner titleText={homeBanner.titleText} onClick={onCallToActionClick} />;
  } else if (ctaType === CTATypes.viewOtc) {
    return <CreditBanner titleText={homeBanner.titleText} />;
  } else if (
    ctaType === CTATypes.trackOrder ||
    ctaType === CTATypes.rescheduleOrder ||
    ctaType === CTATypes.rescheduleOrderInvalidAddress ||
    ctaType === CTATypes.openChat ||
    ctaType === CTATypes.scheduleDelivery
  ) {
    return (
      <OrderBanner
        titleText={homeBanner.titleText}
        subtitleText={homeBanner.subtitleText}
        cta={homeBanner.cta}
        onClick={onCallToActionClick}
      />
    );
  }
  return null;
};
